import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { getPrices } from '../../../lib/getPrices'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import { getBackInStockDate, getExactStockLevel, getProductManualStockLimit } from '../../../lib/product'
import { getSupplierDeliveryDetails } from '../../../lib/service'
import { getBrandName, getProductName } from '../../../lib/text'
import { Product, ProductQuantity, Supplier } from '../../../utils/types/Product'

const useProductQuantity = (
  product: Product | undefined,
  supplier: Supplier | undefined,
): [ProductQuantity[], Dispatch<SetStateAction<ProductQuantity[]>>] => {
  const { locale: countryAndLocaleString } = useRouter()

  const { locale, country } = getCountryAndLocaleStrings(countryAndLocaleString)
  const [productQuantity, setProductQuantity] = useState<ProductQuantity[]>([])

  const moq = product ? getPrices(product, 1).moq : 1
  const { deliveryTime } = getSupplierDeliveryDetails(supplier, country)
  const timeframe = deliveryTime.toString()
  const outOfStock = getBackInStockDate(product)
  const productName = getProductName(product) ?? ''
  const brandName = product ? getBrandName(product) : ''
  const manualStockLimit = product ? getProductManualStockLimit(product) : ''

  useEffect(() => {
    if (product?.product && product.product.length > 0) {
      const variantQuantity = product.product?.map((ele) => ({
        label: ele.attribute[0]?.['attribute.label'] || ele['product.label'],
        quantity: 0,
        id: ele['product.code'],
        manualStockLimit: Number(getProductManualStockLimit(ele)
          ?? manualStockLimit),
        stockDate: getBackInStockDate(ele),
        exactStockLevel: getExactStockLevel(ele) ?? null,
        timeframe,
        productId: product.id,
        // Each variant should have a single variant attribute, can be different types
        variantId: ele.attribute?.[0]?.id,
      }))
      setProductQuantity(variantQuantity)
    } else {
      setProductQuantity([{
        label: productName.replace(brandName, '').trim(),
        quantity: moq,
        id: product?.['product.code'] || '',
        manualStockLimit: Number(manualStockLimit),
        exactStockLevel: getExactStockLevel(product) ?? null,
        stockDate: outOfStock,
        timeframe,
        productId: product?.id || '',
      }])
    }
  }, [product?.product?.length, product?.product?.[0]?.attribute?.[0]?.['attribute.type'], product?.['product.code'], locale, productName])

  useEffect(() => {
    // Update productQuantity if data from api updates
    let hasUpdated = false
    const updated = productQuantity
      .map((quantity, index) => {
        const variant = product?.product?.[index]
        if (variant && quantity.stockDate !== getBackInStockDate(variant)) {
          hasUpdated = true
          // Update productQuantity if new data has different values
          return {
            ...quantity,
            stockDate: getBackInStockDate(variant) || null,
            timeframe,
          }
        }
        return quantity
      })
    if (hasUpdated) setProductQuantity(updated)
  }, [product?.product])

  return [productQuantity, setProductQuantity]
}

export default useProductQuantity
